import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { ProgressStorage } from "@/progress/ProgressStorage";
import { createButton } from "@/ui/Button";
import { contentFlowStartY, layoutButtonRows } from "@/ui/buttonLayout";
import { createHowToPlayOverlay, type HowToPlayOverlay } from "@/ui/HowToPlay";
import { getAudioManager } from "@/audio/AudioManager";
import { ENDLESS_RUN_CONTEXT } from "@/endless/types";
import { attachOrientationGate, destroyLayoutChildren, type OrientationGateHandle } from "@/scenes/OrientationGate";
import { createLegalFooter } from "@/ui/LegalFooter";

/**
 * The full main menu - Campaign, Daily, Endless, Endless Ranks, Campaign
 * leaderboard, HOW TO PLAY, and the sound toggle. Instant Play &
 * Onboarding V1 moved this scene out of `main.ts`'s scene index 0 (see
 * `EntryScene`, now the app's actual entry point) - `MenuScene` no longer
 * performs the one-shot initial-route detection
 * (`consumeInitialRoute`/`consumeAudioLabRoute`) that used to live here;
 * `EntryScene.create()` already consumed that one-shot gate for this page
 * load before a player can ever reach this scene, whether on a normal
 * visit (via MAIN MENU) or a deep link (which never reaches this scene at
 * all). Every button/destination below is otherwise unchanged.
 */
export class MenuScene extends Phaser.Scene {
  private howToPlay!: HowToPlayOverlay;
  private isHowToPlayOpen = false;
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("MenuScene");
  }

  create(): void {
    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.isHowToPlayOpen = false;
    this.buildLayout();
    this.scale.on("resize", this.buildLayout, this);
    this.events.once("shutdown", () => {
      this.scale.off("resize", this.buildLayout, this);
    });
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();
  }

  private buildLayout(): void {
    destroyLayoutChildren(this); // also destroys the old game objects, except the orientation gate overlay - see that module's own doc
    const { width, height } = this.scale;
    const centerX = width / 2;

    const titleSize = Math.max(36, Math.min(64, width * 0.1));
    const title = this.add
      .text(centerX, height * 0.24, "SNAKESCORE", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${titleSize}px`,
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);

    // The prominent tagline directly under the title - larger and bolder
    // than the small supporting line below it, so the title/tagline/
    // supporting-line hierarchy reads clearly at any viewport width.
    const taglineSize = Math.max(18, Math.min(28, width * 0.05));
    const tagline = this.add
      .text(centerX, title.y + titleSize * 0.74, "CHOOSE YOUR BITE", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${taglineSize}px`,
        fontStyle: "bold",
        color: "#5b8cff",
      })
      .setOrigin(0.5);

    const subtitle = this.add
      .text(centerX, tagline.y + taglineSize * 1.1, "Classic snake. Sharpened.", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);

    // Two rows of two, HOW TO PLAY centered beneath - never locked behind
    // campaign progress, so DAILY CHALLENGE sits alongside the primary
    // campaign action, not tucked several rows down. Responsive: two
    // columns whenever there's comfortable touch-target width, one
    // column (stacked) only on a genuinely narrow viewport - see
    // `layoutButtonRows`.
    //
    // `gridStartY` is always derived from `subtitle`'s own measured
    // rendered bottom edge (never a fixed/height-fraction Y that assumes
    // a particular title size) plus a gap that itself grows a little on
    // a taller viewport - so the first button row can never climb into
    // the title block, on any viewport, while still getting a bit more
    // breathing room on a spacious screen than a cramped one. An earlier
    // version capped this at `height * 0.5`, which on a wide-but-short
    // viewport (title/tagline maxed out at their largest clamped size,
    // e.g. a landscape phone) could pull the grid back up ABOVE that
    // measured bottom - a real button/text overlap, not just a spacing
    // preference.
    //
    // `rowHeight` still shrinks on a short viewport - on a narrow *and*
    // short device (e.g. 320x568) the grid falls back to 4 stacked rows,
    // and a spacious fixed 74px row height would run the grid+HOW TO
    // PLAY+hint text past the bottom of the viewport.
    const gridWidth = Math.min(440, width - 32);
    const subtitleBottom = subtitle.y + subtitle.height / 2;
    const gridStartY = contentFlowStartY(subtitleBottom, height, { floor: 34, cap: 58, factor: 0.055 });
    const gridRowHeight = Math.max(56, Math.min(74, height * 0.095));
    const { placements, bottomY } = layoutButtonRows(
      [
        [{}, {}], // CONTINUE, DAILY CHALLENGE
        [{}, {}], // WORLDS, LEADERBOARD
        [{}, {}], // ENDLESS, ENDLESS RANKS - a new row beneath the existing main rows, never replacing CONTINUE
      ],
      { availableWidth: gridWidth, startY: gridStartY, rowHeight: gridRowHeight },
    );

    const [continuePlacement, dailyPlacement] = placements[0];
    const [levelSelectPlacement, leaderboardPlacement] = placements[1];
    const [endlessPlacement, endlessRanksPlacement] = placements[2];
    // Leaves a real gap between stacked rows even at the smallest
    // `gridRowHeight` (56) - never below the 44px accessible minimum
    // touch-target height either way.
    const gridButtonHeight = Math.max(44, gridRowHeight - 8);

    createButton(this, centerX + continuePlacement!.x, continuePlacement!.y, "CONTINUE", () => this.onContinue(), {
      width: continuePlacement!.width,
      height: gridButtonHeight,
    });

    createButton(this, centerX + dailyPlacement!.x, dailyPlacement!.y, "DAILY CHALLENGE", () => this.onDailyChallenge(), {
      width: dailyPlacement!.width,
      height: gridButtonHeight,
      fillColor: GameConfig.colors.star,
      hoverColor: 0xffd166,
      textColor: "#0f1117",
      fontSize: dailyPlacement!.isTwoColumn ? "15px" : "21px",
    });

    createButton(this, centerX + levelSelectPlacement!.x, levelSelectPlacement!.y, "WORLDS", () => this.onPlay(), {
      width: levelSelectPlacement!.width,
      height: gridButtonHeight,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
      fontSize: levelSelectPlacement!.isTwoColumn ? "15px" : "18px",
    });

    createButton(this, centerX + leaderboardPlacement!.x, leaderboardPlacement!.y, "LEADERBOARD", () => this.openLeaderboard(), {
      width: leaderboardPlacement!.width,
      height: gridButtonHeight,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
      fontSize: leaderboardPlacement!.isTwoColumn ? "15px" : "18px",
    });

    createButton(this, centerX + endlessPlacement!.x, endlessPlacement!.y, "ENDLESS", () => this.onEndless(), {
      width: endlessPlacement!.width,
      height: gridButtonHeight,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
      fontSize: endlessPlacement!.isTwoColumn ? "15px" : "18px",
    });

    createButton(this, centerX + endlessRanksPlacement!.x, endlessRanksPlacement!.y, "ENDLESS RANKS", () => this.onEndlessRanks(), {
      width: endlessRanksPlacement!.width,
      height: gridButtonHeight,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
      fontSize: endlessRanksPlacement!.isTwoColumn ? "15px" : "18px",
    });

    createButton(this, centerX, bottomY, "HOW TO PLAY", () => this.openHowToPlay(), {
      width: 220,
      height: 48,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
      fontSize: "16px",
    });

    // Bottom-anchored on a tall viewport (matches the previous fixed
    // 0.86 position), but never lower than just past the button grid -
    // on a short viewport the grid pushes it down instead of overlapping.
    const hintY = Math.max(bottomY + 40, height * 0.86);
    this.add
      .text(centerX, hintY, "Arrow keys to move · Space to pause", {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);

    this.add
      .text(centerX, hintY + 22, "Swipe to move", {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);

    // Rebuilt on every resize along with everything else above - preserve
    // whether it was open across that rebuild.
    this.howToPlay = createHowToPlayOverlay(this, 80, () => this.closeHowToPlay());
    this.howToPlay.setVisible(this.isHowToPlayOpen);

    this.buildSoundToggle(width, height);

    // Launch requirement (PR33): the discreet Privacy/Terms/Contact
    // footer - see `LegalFooter.ts`'s own doc. Anchored to the very
    // bottom edge, below the keyboard/touch hint lines above.
    createLegalFooter(this);
  }

  /** PR13-D production integration: the persistent Sound On/Off control (spec section 9) - top-right corner, small enough to never crowd the title/button grid on desktop or mobile. Rebuilt fresh on every `buildLayout()` call (resize), always reading the current preference so its label never goes stale. Marked `silent` so the shared button-click sound never plays here - `AudioManager.setSoundEnabled()` already plays its own short confirmation sound when turning sound ON, and correctly plays nothing when turning it OFF. */
  private buildSoundToggle(width: number, _height: number): void {
    const toggleWidth = 118;
    const toggleHeight = 38;
    const x = width - toggleWidth / 2 - 16;
    const y = toggleHeight / 2 + 16;
    const button = createButton(this, x, y, getAudioManager().isSoundEnabled() ? "SOUND ON" : "SOUND OFF", () => this.toggleSound(button), {
      width: toggleWidth,
      height: toggleHeight,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
      fontSize: "13px",
      silent: true,
    });
  }

  private toggleSound(button: Phaser.GameObjects.Container): void {
    const next = !getAudioManager().isSoundEnabled();
    getAudioManager().setSoundEnabled(next);
    const labelText = button.list[1] as Phaser.GameObjects.Text;
    labelText.setText(next ? "SOUND ON" : "SOUND OFF");
  }

  private openHowToPlay(): void {
    this.isHowToPlayOpen = true;
    this.howToPlay.setVisible(true);
  }

  private closeHowToPlay(): void {
    this.isHowToPlayOpen = false;
    this.howToPlay.setVisible(false);
  }

  private onDailyChallenge(): void {
    this.scene.start("DailyChallengeScene");
  }

  private onContinue(): void {
    const level = ProgressStorage.getHighestUnlockedLevel();
    this.scene.start("GameScene", { levelId: level.id, runSource: "play" });
  }

  private onPlay(): void {
    this.scene.start("WorldSelectScene");
  }

  /** Starts a fresh Endless run immediately - no attempt reservation, no backend dependency. Never auto-starts and never replaces CONTINUE/Daily routing. */
  private onEndless(): void {
    this.scene.start("GameScene", { endless: ENDLESS_RUN_CONTEXT, runSource: "play" });
  }

  /** Opens the Endless global leaderboard - viewable by anyone, no Online Features opt-in or identity required (see `EndlessLeaderboardService.isLeaderboardAvailable()`). Never touches the existing Campaign `LEADERBOARD` button/destination. */
  private onEndlessRanks(): void {
    this.scene.start("EndlessLeaderboardScene", { returnScene: "MenuScene" });
  }

  private openLeaderboard(): void {
    const level = ProgressStorage.getHighestUnlockedLevel();
    this.scene.start("LeaderboardScene", { levelId: level.id, returnScene: "MenuScene" });
  }
}
